import React from 'react';
import { Marquee } from '../magicui/marquee';
import { BlurFade } from '../magicui/blur-fade';
import { Crosshair, ShieldCheck, Feather, Wrench } from 'lucide-react';

const tags = ['Offline by default', 'No accounts required', 'Windows-native', 'Privacy by design', 'Lightweight', 'Built to last', 'Clear, honest pricing', 'Real human support'];

const pillars = [
  {
    icon: Crosshair,
    title: 'Focused products',
    text: 'We build tools that solve one real problem completely, instead of sprawling suites that do everything halfway.',
  },
  {
    icon: ShieldCheck,
    title: 'Privacy by design',
    text: 'Our software works on your own machine wherever possible. Your files and data stay yours — not ours to collect.',
  },
  {
    icon: Feather,
    title: 'Lean and fast',
    text: 'We respect your hardware. Our products start quickly, run light, and stay out of the way while they work.',
  },
  {
    icon: Wrench,
    title: 'Built to last',
    text: 'We maintain what we ship — with careful updates, clear documentation and support from the people who built it.',
  },
];

export default function Principles() {
  return (
    <section id="company" className="bg-mist">
      <div className="border-y border-line bg-white py-3">
        <Marquee pauseOnHover className="[--duration:35s] [--gap:2.5rem]">
          {tags.map((t) => (
            <span key={t} className="flex items-center gap-2.5 whitespace-nowrap font-mono text-xs uppercase tracking-[0.14em] text-ink-3">
              <span className="size-1.5 rounded-full bg-gradient-to-r from-fusion-blue to-fusion-teal" />
              {t}
            </span>
          ))}
        </Marquee>
      </div>

      <div className="container-x py-24 lg:py-32">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
          <div>
            <BlurFade inView>
              <p className="eyebrow">The company</p>
            </BlurFade>
            <BlurFade inView delay={0.1}>
              <h2 className="h-section mt-5">
                Practical software, <span className="fusion-text">built with care.</span>
              </h2>
            </BlurFade>
            <BlurFade inView delay={0.2}>
              <p className="mt-6 text-lg leading-relaxed text-ink-3">
                Market Fusion Innovations LLC is an independent software company based in Kyiv, Ukraine. We fuse
                thoughtful design with solid engineering to create products people rely on every day.
              </p>
            </BlurFade>
            <BlurFade inView delay={0.25}>
              <p className="mt-4 leading-relaxed text-ink-3">
                Our work starts with a simple question: what is slowing people down? Then we build the smallest,
                most reliable tool that removes it.
              </p>
            </BlurFade>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {pillars.map(({ icon: Icon, title, text }, i) => (
              <BlurFade key={title} inView delay={0.08 * i} className="h-full">
                <div className="group h-full rounded-3xl bg-white p-7 shadow-card transition-shadow duration-300 hover:shadow-lift">
                  <span className="inline-flex size-11 items-center justify-center rounded-2xl bg-mist text-ink transition-colors group-hover:bg-ink group-hover:text-white">
                    <Icon className="size-5" aria-hidden="true" />
                  </span>
                  <h3 className="mt-6 font-display text-xl font-semibold tracking-[-0.02em]">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{text}</p>
                </div>
              </BlurFade>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
